import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SurveyForm from './SurveyForm';
import { backendUrl } from '../../utils/socketClient';

interface SurveyData {
  age_group: string;
  gender: string;
  party_size: string;
  visit_purpose: string;
  time_period: string;
  food_rating: number;
  good_dishes: string;
  service_rating: number;
  service_notes: string;
  atmosphere_rating: number;
  atmosphere_notes: string;
  budget_per_person: string;
  general_feedback: string;
}

interface SurveySettings {
  enabled: boolean;
  welcome_message: string;
  thanks_message: string;
  email_required: boolean;
  age_group_required: boolean;
  visit_frequency_required: boolean;
  gender_required: boolean;
}

const CustomerSurvey: React.FC = () => {
  const { shopName } = useParams<{ shopName: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 設定を読み込み
  useEffect(() => {
    if (shopName) {
      loadSurveySettings();
      document.title = `アンケート | RepliGo（レプリゴ）`;
    }
  }, [shopName]);

  const loadSurveySettings = async () => {
    setLoading(true);
    try {
      // 店舗のユーザーメールを特定する必要があります
      // 実際の実装では、公開URLから店舗設定を取得する専用APIが必要
      const response = await fetch(`${backendUrl}/survey-public-settings/${encodeURIComponent(shopName)}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSettings(result.settings);
          
          // アンケートが無効化されている場合
          if (!result.settings.enabled) {
            setError('現在、アンケートは実施しておりません。');
            return;
          }
        } else {
          setError('店舗情報の取得に失敗しました。');
        }
      } else {
        setError('店舗が見つかりません。');
      }
    } catch (err) {
      // console.error('設定読み込みエラー:', err);
      setError('アンケート設定の読み込み中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData: SurveyData) => {
    if (!shopName) return;

    // console.log('送信データ:', formData);
    // console.log('送信先URL:', `${backendUrl}/api/survey/${encodeURIComponent(shopName)}`);

    setIsSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/survey/${encodeURIComponent(shopName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      // console.log('レスポンス status:', response.status);
      // console.log('レスポンス headers:', response.headers);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      // console.log('レスポンス結果:', result);

      if (result.success) {
        // デバッグ情報をコンソールに表示
        if (result.debug_info) {
          console.log('=== アンケート送信デバッグ情報 ===');
          console.log('Gemini プロンプト:', result.debug_info.prompt);
          console.log('アンケートデータ:', result.debug_info.survey_data);
          console.log('計算された評価:', result.debug_info.ratings);
          console.log('生成された下書き:', result.debug_info.generated_content);
          console.log('==========================');
        }
        // 即座に下書きページへ遷移
        navigate(result.redirect_url);
      } else {
        alert('エラーが発生しました: ' + (result.error || '不明なエラー'));
      }
    } catch (error) {
      // console.error('Submit error:', error);
      alert('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shopName) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">エラー</h2>
          <p className="text-gray-600 mt-2">店舗名が指定されていません</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">アンケート設定を読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">お知らせ</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">エラー</h2>
          <p className="text-gray-600 mt-2">設定情報が取得できませんでした</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-4">
        {/* ヘッダーセクション */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-6 md:p-8 border border-indigo-100">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
              {decodeURIComponent(shopName)}
            </h1>
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-700 mb-4">お客様アンケート</h2>
            <div className="max-w-2xl mx-auto">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
                {settings.welcome_message.split('\n').map((line, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed">{line}</p>
                ))}
              </div>
            </div>
            <div className="mt-6 flex justify-center items-center space-x-8 text-sm text-gray-500">
              <div className="flex items-center">
                <span>所要時間：約3分</span>
              </div>
              <div className="flex items-center">
                <span>AI下書き生成</span>
              </div>
              <div className="flex items-center">
                <span>スマホ対応</span>
              </div>
            </div>
          </div>
        </div>

        {/* アンケートフォーム */}
        <SurveyForm 
          onSubmit={handleSubmit} 
          isSubmitting={isSubmitting}
          shopName={decodeURIComponent(shopName)}
          settings={settings}
        />
      </div>
    </div>
  );
};

export default CustomerSurvey;